/**
 * Size of the floating Notification Center (#1133).
 *
 * While open, the panel has a fixed height instead of following its content,
 * so switching a filter, "Nur neue", expanding a bundle or opening the
 * settings never resizes it. Maximised, it takes the full height between the
 * top inset and the taskbar and grows wider, still anchored bottom right.
 *
 * The maximised choice is a per-browser layout preference, so it lives in
 * `localStorage`, not the cloud settings.
 *
 * Two anchors (#1169): `taskbar-right` in the desktop, and `rail-left` under
 * the shell-free routes, where there is no taskbar and the bell sits in the
 * icon rail — the panel then floats bottom left, next to the rail, and uses
 * the full height. Size rules (fixed height, maximise) are the same for both.
 */
import type React from 'react'
import { TASKBAR_HEIGHT } from '@/shared/ui'

/** Distance to the screen edges and to the taskbar. */
export const PANEL_INSET_PX = 12
export const PANEL_WIDTH_PX = 410
export const PANEL_MAX_HEIGHT_PX = 720
export const PANEL_MAXIMIZED_WIDTH_PX = 720

export const PANEL_MAXIMIZED_STORAGE_KEY = 'cdb-notification-panel-maximized'

/** Where the panel (and its toasts) float: above the desktop taskbar, or beside the standalone rail. */
export type PanelAnchor = 'taskbar-right' | 'rail-left'

/** Width of the icon rail of the shell-free routes (`StandaloneLayout`, `w-14`). */
export const STANDALONE_RAIL_WIDTH_PX = 56

/** What the anchor reserves at the bottom (taskbar) and at the left (rail). */
function reserved(anchor: PanelAnchor): { bottom: number; left: number } {
  return anchor === 'rail-left' ? { bottom: 0, left: STANDALONE_RAIL_WIDTH_PX } : { bottom: TASKBAR_HEIGHT, left: 0 }
}

export function panelGeometry(maximized: boolean, anchor: PanelAnchor = 'taskbar-right'): React.CSSProperties {
  const { bottom, left } = reserved(anchor)
  /** The height between the top inset and the inset above the taskbar (or the screen edge). */
  const availableHeight = `calc(100vh - ${bottom + 2 * PANEL_INSET_PX}px)`
  const availableWidth = `calc(100vw - ${left + 2 * PANEL_INSET_PX}px)`
  return {
    ...(anchor === 'rail-left' ? { left: `${left + PANEL_INSET_PX}px` } : { right: `${PANEL_INSET_PX}px` }),
    bottom: `${bottom + PANEL_INSET_PX}px`,
    width: `min(${maximized ? PANEL_MAXIMIZED_WIDTH_PX : PANEL_WIDTH_PX}px, ${availableWidth})`,
    height: maximized ? availableHeight : `min(${PANEL_MAX_HEIGHT_PX}px, ${availableHeight})`,
  }
}

/** Distance of the toast stack to the screen edges (and to the taskbar or rail). */
export const TOAST_INSET_PX = 16

/** Position of the toast stack: bottom right above the taskbar, or bottom left beside the rail. */
export function toastGeometry(anchor: PanelAnchor = 'taskbar-right'): React.CSSProperties {
  const { bottom, left } = reserved(anchor)
  return {
    ...(anchor === 'rail-left' ? { left: `${left + TOAST_INSET_PX}px` } : { right: `${TOAST_INSET_PX}px` }),
    bottom: `${bottom + TOAST_INSET_PX}px`,
  }
}

/** Remembered choice; anything but "true" (or unreadable storage) means normal size. */
export function readStoredMaximized(storage?: Pick<Storage, 'getItem'>): boolean {
  try {
    return (storage ?? localStorage).getItem(PANEL_MAXIMIZED_STORAGE_KEY) === 'true'
  } catch {
    // Blocked site data or private mode throws on access; the normal size is always safe.
    return false
  }
}

/** Best effort: a full or blocked store must not break the toggle. */
export function storeMaximized(maximized: boolean, storage?: Pick<Storage, 'setItem'>): void {
  try {
    ;(storage ?? localStorage).setItem(PANEL_MAXIMIZED_STORAGE_KEY, String(maximized))
  } catch {
    // The choice still applies for this session.
  }
}
